import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { 
  Flame, 
  Eye, 
  Timer, 
  Megaphone, 
  Zap, 
  BarChart3, 
  Users, 
  Flag, 
  Trophy,
  Coins,
  Compass,
  ScrollText,
  Radio,
  Sparkles,
  Shield,
  ScanEye,
  Network,
  Rocket,
  Scale,
  Code,
  Route,
  Palette
} from 'lucide-react';

// Icon mapping for each card
const cardIcons = {
  torch: Flame,
  crystal: Eye,
  hourglass: Timer,
  megaphone: Megaphone,
  lightning: Zap,
  chart: BarChart3,
  hands: Users,
  flag: Flag,
  chess: Trophy,
  coin: Coins,
  // New card icons
  compass: Compass,
  scroll: ScrollText,
  broadcast: Radio,
  sparkles: Sparkles,
  shield: Shield,
  eye: ScanEye,
  network: Network,
  rocket: Rocket,
  balance: Scale,
  code: Code,
  road: Route,
  palette: Palette
};

// Color schemes for cards based on the marketing tarot style
const colorSchemes = {
  purple: {
    bg: 'from-[#3d2c5e] to-[#2a1f42]',
    accent: '#9b7ed9',
    iconBg: 'bg-[#4a3870]'
  },
  teal: {
    bg: 'from-[#1a4f5c] to-[#0f3640]',
    accent: '#5cb8c9',
    iconBg: 'bg-[#236b7a]'
  },
  navy: {
    bg: 'from-[#1e2a4a] to-[#141c33]',
    accent: '#6b8cce',
    iconBg: 'bg-[#2a3a5f]'
  },
  orange: {
    bg: 'from-[#8b4513] to-[#5c2d0e]',
    accent: '#e89b47',
    iconBg: 'bg-[#a05a1f]'
  }
};

export const MarketingTarotCard = ({ 
  card, 
  isFlipped = false, 
  onClick, 
  className,
  style,
  isHoverable = true,
  size = 'default'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Return null if no card data
  if (!card) return null;
  
  const sizeClasses = {
    small: 'w-28 h-44',
    medium: 'w-36 h-52',
    default: 'w-40 h-60',
    large: 'w-52 h-80'
  };

  const iconSizes = {
    small: 'w-10 h-10',
    medium: 'w-12 h-12',
    default: 'w-16 h-16',
    large: 'w-20 h-20'
  };

  const fontSizes = {
    small: { title: 'text-[10px]', the: 'text-[8px]' },
    medium: { title: 'text-xs', the: 'text-[9px]' },
    default: { title: 'text-sm', the: 'text-xs' },
    large: { title: 'text-lg', the: 'text-sm' }
  };

  const IconComponent = cardIcons[card?.icon] || Flame;
  const colors = colorSchemes[card?.color] || colorSchemes.purple;
  const fonts = fontSizes[size] || fontSizes.default;

  return (
    <div 
      className={cn(
        "cursor-pointer select-none",
        sizeClasses[size],
        className
      )}
      style={{ ...style, perspective: '1500px' }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="relative w-full h-full"
        style={{ 
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Card Back */}
        <div 
          className={cn(
            "absolute inset-0 rounded-xl overflow-hidden",
            "border-4 border-[#d4c4a8]",
            "shadow-lg",
            isHoverable && isHovered && !isFlipped && "shadow-2xl scale-105"
          )}
          style={{ 
            backfaceVisibility: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            background: 'linear-gradient(135deg, #1e2a4a 0%, #141c33 100%)'
          }}
        >
          {/* Decorative back pattern */}
          <div className="absolute inset-3 border-2 border-[#d4c4a8]/30 rounded-lg flex items-center justify-center">
            <div className="relative">
              {/* Outer circle */}
              <div className="w-20 h-20 rounded-full border-2 border-[#e89b47]/60 flex items-center justify-center">
                {/* Inner circle */}
                <div className="w-14 h-14 rounded-full border border-[#e89b47]/40 flex items-center justify-center">
                  {/* Center star */}
                  <div className="text-[#e89b47] text-3xl">✦</div>
                </div>
              </div>
              {/* Corner decorations */}
              <div className="absolute -top-8 -left-8 w-4 h-4 border-t-2 border-l-2 border-[#d4c4a8]/40"/>
              <div className="absolute -top-8 -right-8 w-4 h-4 border-t-2 border-r-2 border-[#d4c4a8]/40"/>
              <div className="absolute -bottom-8 -left-8 w-4 h-4 border-b-2 border-l-2 border-[#d4c4a8]/40"/>
              <div className="absolute -bottom-8 -right-8 w-4 h-4 border-b-2 border-r-2 border-[#d4c4a8]/40"/>
            </div>
          </div>
          {/* Grain texture overlay */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" 
               style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }}/>
        </div>

        {/* Card Front */}
        <div 
          className={cn(
            "absolute inset-0 rounded-xl overflow-hidden flex flex-col",
            "border-4 border-[#d4c4a8]",
            "shadow-lg",
            card?.isReversed && "rotate-180"
          )}
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          {/* Illustration Section - Top 70% */}
          <div 
            className={cn("flex-1 flex items-center justify-center relative bg-gradient-to-b", colors.bg)}
            style={{ minHeight: '70%' }}
          >
            {/* Icon container */}
            <div className={cn(
              "rounded-full flex items-center justify-center",
              colors.iconBg,
              iconSizes[size] === 'w-10 h-10' ? 'p-3' : iconSizes[size] === 'w-16 h-16' ? 'p-5' : 'p-6'
            )}>
              <IconComponent 
                className={cn(iconSizes[size], "text-[#e89b47]")}
                strokeWidth={1.5}
              />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-3 left-3 text-[#d4c4a8]/30 text-lg">✦</div>
            <div className="absolute top-3 right-3 text-[#d4c4a8]/30 text-lg">✦</div>
            <div className="absolute bottom-3 left-3 text-[#d4c4a8]/30 text-lg">✦</div>
            <div className="absolute bottom-3 right-3 text-[#d4c4a8]/30 text-lg">✦</div>
            
            {/* Grain texture */}
            <div className="absolute inset-0 opacity-5 pointer-events-none" 
                 style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.7\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }}/>
          </div>

          {/* Name Section - Bottom 30% */}
          <div 
            className="bg-[#e8dcc8] flex flex-col items-center justify-center py-3 px-2 border-t-2 border-[#c4b396]"
            style={{ minHeight: '30%' }}
          >
            <span className={cn("text-[#5c4a32] tracking-widest font-medium", fonts.the)}>
              THE
            </span>
            <span className={cn(
              "text-[#2a1f14] font-bold tracking-wider text-center leading-tight uppercase",
              fonts.title
            )}>
              {card?.name?.replace('The ', '') || 'CARD'}
            </span>
            {card?.isReversed && (
              <span className="text-[#8b6b4a] text-[8px] mt-1 italic">(Reversed)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingTarotCard;
